import classNames from 'classnames';
import {
  TitledBlock,
  FlexContainer,
  Button,
  SelectButtons,
  SelectButton,
  Txt,
  InputLocation,
} from 'components';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useMapCenterHelper, useSearchMainStateHelpers } from '../../hooks';
import {
  rentOptions,
  parkingOptions,
  outsideSpaceOptions,
  featuresOptions,
  bedroomOptions,
  furnishingOptions,
  typeOptions,
  dateAddedOptions,
} from 'config/propertyFields';

import styles from './SearchFilterForm.module.scss';

/**
 * SearchFilterForm
 *
 * @return {JSX.Element}
 */
const SearchFilterForm = ({ className }) => {
  const {
    state: { searchFilterState, searchBrowserFilterState },
    handleSubmit,
    handleChange,
  } = useSearchMainStateHelpers();

  useEffect(() => () => searchFilterState.resetFilter(), [searchFilterState]);

  const handleMap = useMapCenterHelper(searchBrowserFilterState.state.location);

  const rentMinOptions = useMemo(
    () =>
      searchFilterState.state.tenancyRentAmountMax
        ? rentOptions.filter(
            ({ key }) =>
              !key || key <= searchFilterState.state.tenancyRentAmountMax
          )
        : rentOptions,
    [searchFilterState.state.tenancyRentAmountMax]
  );

  const rentMaxOptions = useMemo(
    () =>
      searchFilterState.state.tenancyRentAmountMin
        ? rentOptions.filter(
            ({ key }) =>
              !key || key >= searchFilterState.state.tenancyRentAmountMin
          )
        : rentOptions,
    [searchFilterState.state.tenancyRentAmountMin]
  );

  const { location } = searchBrowserFilterState.state;

  return (
    <FlexContainer
      className={classNames(
        styles.SearchFilterForm,
        className,
        `${SearchFilterForm.displayName}`
      )}
      column
    >
      <form onSubmit={handleSubmit}>
        <TitledBlock title="Area">
          <InputLocation
            initialValue={location.bounds ? undefined : location}
            name="location"
            onChange={handleChange}
            whenCreated={handleMap}
            minZoom={7}
            maxZoom={15}
            bounds={
              location.bounds
                ? [
                    [location.bounds.latMin, location.bounds.lngMin],
                    [location.bounds.latMax, location.bounds.lngMax],
                  ]
                : undefined
            }
            // zoomControl={false}
          />
        </TitledBlock>
        <TitledBlock title="Rent per month">
          <FlexContainer gap>
            <SelectButton
              className={styles.SelectButton}
              options={rentMinOptions}
              name="tenancyRentAmountMin"
              value={searchFilterState.state.tenancyRentAmountMin || ''}
              onChange={handleChange}
              label="Minimum"
            />
            <SelectButton
              className={styles.SelectButton}
              options={rentMaxOptions}
              name="tenancyRentAmountMax"
              value={searchFilterState.state.tenancyRentAmountMax || ''}
              onChange={handleChange}
              label="Maximum"
            />
          </FlexContainer>
        </TitledBlock>
        <TitledBlock title="Bedrooms">
          <SelectButtons
            options={bedroomOptions}
            onChange={handleChange}
            value={searchFilterState.state.bedroomCount || []}
            name="bedroomCount"
            multiple
            allButton
          />
        </TitledBlock>
        <TitledBlock title="Bathrooms">
          <SelectButtons
            options={bedroomOptions}
            onChange={handleChange}
            value={searchFilterState.state.bathroomCount || []}
            name="bathroomCount"
            multiple
            allButton
          />
        </TitledBlock>
        <TitledBlock title="Type">
          <SelectButtons
            options={typeOptions}
            onChange={handleChange}
            value={searchFilterState.state.propertyType || []}
            name="propertyType"
            multiple
            allButton
          />
        </TitledBlock>
        <TitledBlock title="Furnishing">
          <SelectButtons
            options={furnishingOptions}
            onChange={handleChange}
            value={searchFilterState.state.propertyFurnishing || []}
            name="propertyFurnishing"
            multiple
            allButton
          />
        </TitledBlock>
        <TitledBlock title="Features">
          <SelectButtons
            options={featuresOptions}
            onChange={handleChange}
            value={searchFilterState.state.propertyFeatures || []}
            name="propertyFeatures"
            multiple
          />
        </TitledBlock>
        <TitledBlock title="Outside space">
          <SelectButtons
            options={outsideSpaceOptions}
            onChange={handleChange}
            value={searchFilterState.state.propertyOutsideSpace || []}
            name="propertyOutsideSpace"
            multiple
            allButton
          />
        </TitledBlock>
        <TitledBlock title="Parking">
          <SelectButtons
            options={parkingOptions}
            onChange={handleChange}
            value={searchFilterState.state.propertyParking || []}
            name="propertyParking"
            multiple
            allButton
          />
        </TitledBlock>
        <TitledBlock title="Date Added">
          <SelectButtons
            options={dateAddedOptions}
            onChange={handleChange}
            value={searchFilterState.state.dateAdded || ''}
            name="dateAdded"
            allButton
            allButtonTitle="Any Time"
          />
        </TitledBlock>
        <FlexContainer>
          <Button type="submit" flex success>
            <Txt xx bold>
              Search
            </Txt>
          </Button>
        </FlexContainer>
      </form>
    </FlexContainer>
  );
};

SearchFilterForm.displayName = 'SearchFilterForm';

export default observer(SearchFilterForm);
